import React, { useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { submitContact } from "../lib/api";
import { MapPin, Phone, Smartphone, Mail, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";

export default function Iletisim() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState({ loading: false, ok: false, error: null });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: null });
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        subject: form.subject || null,
        message: form.message,
      });
      setStatus({ loading: false, ok: true, error: null });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Mesajınız gönderilemedi. Lütfen tekrar deneyin.";
      setStatus({ loading: false, ok: false, error: typeof msg === "string" ? msg : "Bir hata oluştu." });
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Bize Ulaşın"
        title="İletişim"
        description="Soru, talep ve önerileriniz için 7 gün 24 saat ulaşılabiliriz."
      />

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Contact info */}
            <aside className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.25em] text-brand-gold font-bold mb-4">
                Adres ve İletişim
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-slate-950 tracking-tighter leading-tight mb-8">
                Yalova merkezimize bekliyoruz.
              </h2>

              <div className="space-y-5 mb-10">
                <div className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-200">
                  <MapPin className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Adres</div>
                    <div className="text-slate-950 font-medium">
                      Fevzi Çakmak Mah. İstanbul Cad. No:4, 77100 Merkez/Yalova
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <a
                    href="tel:+902268112950"
                    className="p-5 rounded-xl bg-slate-950 text-white hover:bg-brand-gold transition-colors block"
                    data-testid="contact-phone-link"
                  >
                    <Phone className="w-5 h-5 text-brand-gold mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-300 mb-1">Telefon</div>
                    <div className="font-mono-fin font-semibold">0226 811 29 50</div>
                  </a>
                  <a
                    href="tel:+905400804977"
                    className="p-5 rounded-xl bg-slate-950 text-white hover:bg-brand-gold transition-colors block"
                  >
                    <Smartphone className="w-5 h-5 text-brand-gold mb-2" />
                    <div className="text-[11px] uppercase tracking-wider text-slate-300 mb-1">GSM</div>
                    <div className="font-mono-fin font-semibold">0540 080 49 77</div>
                  </a>
                </div>

                <div className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-200">
                  <Mail className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">E-posta</div>
                    <a href="mailto:info@akaydindoviz.com" className="text-slate-950 font-medium hover:text-brand-gold">
                      info@akaydindoviz.com
                    </a>
                  </div>
                </div>

                <div className="flex gap-4 p-5 rounded-xl bg-slate-50 border border-slate-200">
                  <Clock className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Çalışma Saatleri</div>
                    <div className="text-slate-950 font-medium">Pzt-Cmt: 09:00 - 18:30 · Pazar Kapalı</div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3]">
                <iframe
                  title="Akaydın Döviz Yalova Konum Haritası"
                  src="https://maps.google.com/maps?q=Akayd%C4%B1n+D%C3%B6viz&ll=40.6556461,29.2710678&z=17&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </aside>

            {/* Form */}
            <div className="lg:col-span-7">
              <form
                onSubmit={onSubmit}
                className="p-8 md:p-10 rounded-2xl bg-white border border-slate-200 shadow-sm"
                data-testid="contact-form"
              >
                <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-950 mb-2 tracking-tight">
                  Bize Yazın
                </h3>
                <p className="text-sm text-slate-500 mb-8">En kısa sürede dönüş sağlanacaktır.</p>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Ad Soyad" name="name" value={form.name} onChange={onChange} required testId="contact-name" />
                  <Field label="E-posta" name="email" type="email" value={form.email} onChange={onChange} required testId="contact-email" />
                  <Field label="Telefon" name="phone" value={form.phone} onChange={onChange} testId="contact-phone-input" />
                  <Field label="Konu" name="subject" value={form.subject} onChange={onChange} testId="contact-subject" />
                </div>

                <div className="mt-4">
                  <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2 block">
                    Mesajınız
                  </label>
                  <textarea
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    required
                    minLength={5}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20 text-slate-950"
                    data-testid="contact-message"
                  />
                </div>

                {status.error && (
                  <div className="mt-4 p-3 rounded-xl bg-rose-50 text-rose-700 text-sm border border-rose-200">
                    {status.error}
                  </div>
                )}
                {status.ok && (
                  <div className="mt-4 p-4 rounded-xl bg-emerald-50 text-emerald-700 text-sm border border-emerald-200 flex items-center gap-2" data-testid="contact-success">
                    <CheckCircle2 className="w-4 h-4" />
                    Mesajınız başarıyla iletildi. En kısa sürede size dönüş yapacağız.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status.loading}
                  className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-950 text-white text-sm font-semibold hover:bg-brand-gold transition-colors disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {status.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Mesajı Gönder
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2 block">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20 text-slate-950"
        data-testid={testId}
      />
    </div>
  );
}
